import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SecurityAlertsPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate real-time security alerts
    const mockAlerts = [
      {
        id: 1,
        type: "breach_notification",
        title: "Data Breach Detected",
        message: "12 credentials found in recent LinkedIn data breach",
        severity: "critical",
        timestamp: new Date(Date.now() - 180000),
        actionRequired: true,
        affectedUsers: 12,
        source: "HaveIBeenPwned"
      },
      {
        id: 2,
        type: "suspicious_login",
        title: "Suspicious Login Attempt",
        message: "Multiple failed login attempts from IP 203.0.113.45",
        severity: "warning",
        timestamp: new Date(Date.now() - 420000),
        actionRequired: true,
        affectedUsers: 1,
        source: "Login Monitor"
      },
      {
        id: 3,
        type: "policy_compliance",
        title: "Password Policy Violation",
        message: "15 passwords haven't been rotated in 90+ days",
        severity: "warning",
        timestamp: new Date(Date.now() - 1800000),
        actionRequired: true,
        affectedUsers: 8,
        source: "Policy Engine"
      },
      {
        id: 4,
        type: "weak_password",
        title: "Weak Passwords Detected",
        message: "7 passwords scored below security threshold",
        severity: "medium",
        timestamp: new Date(Date.now() - 3600000),
        actionRequired: false,
        affectedUsers: 7,
        source: "Password Analyzer"
      },
      {
        id: 5,
        type: "sharing_alert",
        title: "External Sharing Alert",
        message: "Sensitive vault shared with external domain",
        severity: "critical",
        timestamp: new Date(Date.now() - 7200000),
        actionRequired: true,
        affectedUsers: 1,
        source: "Sharing Monitor"
      }
    ];

    setTimeout(() => {
      setAlerts(mockAlerts);
      setIsLoading(false);
    }, 800);
  }, []);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-error';
      case 'warning': return 'text-warning';
      case 'medium': return 'text-accent';
      default: return 'text-muted-foreground';
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-error/10 border-error/20';
      case 'warning': return 'bg-warning/10 border-warning/20';
      case 'medium': return 'bg-accent/10 border-accent/20';
      default: return 'bg-muted/50 border-border';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'breach_notification': return 'ShieldAlert';
      case 'suspicious_login': return 'UserX';
      case 'policy_compliance': return 'AlertCircle';
      case 'weak_password': return 'Key';
      case 'sharing_alert': return 'Share2';
      default: return 'Bell';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return timestamp?.toLocaleDateString();
    }
  };

  const handleAlertAction = (alert) => {
    console.log('Taking action on alert:', alert);
  };

  const handleDismissAlert = (alertId) => {
    setAlerts(alerts?.filter(alert => alert?.id !== alertId));
  };

  const handleViewAllAlerts = () => {
    console.log('Navigating to security alerts page...');
  };

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Security Alerts</h2>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-muted rounded animate-pulse"></div>
          </div>
        </div>
        <div className="space-y-4">
          {[...Array(4)]?.map((_, i) => (
            <div key={i} className="p-4 border border-muted rounded-lg animate-pulse">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-muted rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const criticalAlerts = alerts?.filter(alert => alert?.severity === 'critical')?.length;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h2 className="text-lg font-semibold text-foreground">Security Alerts</h2>
          {criticalAlerts > 0 && (
            <span className="bg-error text-error-foreground text-xs px-2 py-1 rounded-full">
              {criticalAlerts} Critical
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={handleViewAllAlerts}>
          View All
        </Button>
      </div>
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {alerts?.map((alert) => (
          <div
            key={alert?.id}
            className={`p-4 rounded-lg border transition-smooth ${getSeverityBg(alert?.severity)}`}
          >
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-full ${getSeverityBg(alert?.severity)}`}>
                <Icon 
                  name={getAlertIcon(alert?.type)} 
                  size={16} 
                  className={getSeverityColor(alert?.severity)}
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    {alert?.title}
                  </h3>
                  <button
                    onClick={() => handleDismissAlert(alert?.id)}
                    className="text-muted-foreground hover:text-foreground transition-smooth"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
                
                <p className="text-sm text-muted-foreground mb-3">
                  {alert?.message}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>{formatTimestamp(alert?.timestamp)}</span>
                    <span>{alert?.affectedUsers} users affected</span>
                    <span>Source: {alert?.source}</span>
                  </div>
                  
                  {alert?.actionRequired && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAlertAction(alert)}
                    >
                      Take Action
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {alerts?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Shield" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No security alerts at this time</p>
          <p className="text-sm text-muted-foreground mt-1">Your organization is secure</p>
        </div>
      )}
    </div>
  );
};

export default SecurityAlertsPanel;